(function () {
  const COOKIE_NAME = 'fbt_collapsed';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const ACCORDION_TRANSITION_MS = 350;

  const COMPLEMENTARY_OPTION_MATCH_RULES = [
    {
      sourceOptions: ['Frame Finish'],
      targetOptions: ['Frame Finish', 'Base Color'],
    },
  ];

  function parsePayload(root) {
    const payload = root.querySelector('.frequently-bought-together__payload');
    if (!payload || !payload.textContent.trim()) return {};

    try {
      return JSON.parse(payload.textContent);
    } catch (error) {
      console.error('frequently-bought-together: invalid payload', error);
      return {};
    }
  }

  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value) {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}`;
  }

  function finishExpandedContent(content) {
    if (content.classList.contains('expanded')) {
      content.style.height = 'auto';
      content.style.overflow = 'visible';
    }
  }

  function handleContentTransitionEnd(event) {
    if (event.propertyName !== 'height') return;

    finishExpandedContent(event.currentTarget);
  }

  function findCurrentProductVariant(payload, variantId) {
    const variants = payload?.currentProduct?.variants || [];
    return variants.find((variant) => String(variant.id) === String(variantId));
  }

  function findComplementaryProductPayload(payload, productId) {
    const products = payload?.complementaryProducts || [];
    return products.find((product) => String(product.productId) === String(productId));
  }

  function findVariantInProductPayload(productPayload, variantId) {
    const variants = productPayload?.variants || [];
    return variants.find((variant) => String(variant.id) === String(variantId));
  }

  function getCurrentVariantId(event) {
    if (event?.data?.variant?.id) return event.data.variant.id;

    const variantInput = document.querySelector('.buy-buttons form input.product-variant-id');
    return variantInput ? variantInput.value : null;
  }

  function getLineQty(productPayload, variant) {
    const minSaleQty = Number(productPayload?.minSaleQty) || 1;
    const quantityRuleMin = Number(variant?.quantityRuleMin) || 1;
    return Math.max(minSaleQty, quantityRuleMin);
  }

  function isVariantSellable(variant) {
    if (!variant) return false;
    if (variant.available === false) return false;
    if (variant.quantityRuleSoldout === true) return false;
    return true;
  }

  function normalizeOptionName(name) {
    return String(name || '')
      .trim()
      .toLowerCase();
  }

  function getOptionIndex(optionNames, candidates) {
    const names = Array.isArray(optionNames) ? optionNames : [];
    const normalizedNames = names.map(normalizeOptionName);

    for (const candidate of candidates || []) {
      const normalizedCandidate = normalizeOptionName(candidate);
      const index = normalizedNames.indexOf(normalizedCandidate);
      if (index !== -1) return index;
    }

    return -1;
  }

  function getVariantOptionValue(variant, index) {
    if (!variant || index < 0) return '';
    const options = Array.isArray(variant.options) ? variant.options : [];
    return options[index] != null ? String(options[index]) : '';
  }

  function getOptionValueMatchRank(sourceValue, targetValue) {
    const source = normalizeOptionName(sourceValue);
    const target = normalizeOptionName(targetValue);
    if (!source || !target) return -1;

    if (source === target) {
      return 100000 + target.length;
    }

    if (source.endsWith(' ' + target)) {
      return target.length;
    }

    return -1;
  }

  function optionValuesMatch(sourceValue, targetValue) {
    return getOptionValueMatchRank(sourceValue, targetValue) >= 0;
  }

  function compareOptionValueMatch(sourceValue, targetValueA, targetValueB) {
    const rankA = getOptionValueMatchRank(sourceValue, targetValueA);
    const rankB = getOptionValueMatchRank(sourceValue, targetValueB);

    if (rankA < 0 && rankB < 0) return 0;
    if (rankA < 0) return 1;
    if (rankB < 0) return -1;

    return rankB - rankA;
  }

  function findBestSellableMatchForTargetOption(sourceValue, variants, targetIndex) {
    let bestVariant = null;
    let bestRank = -1;

    for (const variant of variants || []) {
      if (!isVariantSellable(variant)) continue;

      const targetValue = getVariantOptionValue(variant, targetIndex);
      const rank = getOptionValueMatchRank(sourceValue, targetValue);
      if (rank < bestRank) continue;

      if (rank > bestRank) {
        bestRank = rank;
        bestVariant = variant;
      }
    }

    return bestVariant;
  }

  function resolveVariantImage(productPayload, variant) {
    if (variant?.image?.src) return variant.image;
    if (productPayload?.fallbackImage?.src) return productPayload.fallbackImage;
    return null;
  }

  function syncItemImage(item, productPayload, variant) {
    const imageEl = item.querySelector('[data-fbt-image]');
    if (!imageEl) return;

    const image = resolveVariantImage(productPayload, variant);
    if (!image?.src) return;

    imageEl.src = image.src;
    if (image.alt) imageEl.alt = image.alt;
    if (image.srcset) imageEl.srcset = image.srcset;
  }

  function getPropertyName(input) {
    const match = input.name.match(/^properties\[(.+)]$/);
    return match ? match[1] : '';
  }

  function setPropertyValue(item, propertyName, value) {
    const input = item.querySelector(`[data-fbt-property="${propertyName}"]`);
    if (input) input.value = value == null ? '' : String(value);
  }

  function buildAvailabilityMessage(variant) {
    if (variant?.isCustom === true) {
      return `Custom order.${variant.leadTime ? ` Ships in ${variant.leadTime}` : ''}`;
    }

    return '';
  }

  async function refreshAvailabilityMessage(item, variant, quantity) {
    if (typeof window.getProductAvailabilityMessages !== 'function' || !variant) return;

    try {
      const { cartLineItemAvailabilityMessage } = await window.getProductAvailabilityMessages(
        variant.sku || '',
        quantity,
        Number(variant.inventoryQuantity) || 0,
        variant.leadTime != null ? String(variant.leadTime) : '',
        variant.isCustom
      );

      if (cartLineItemAvailabilityMessage) {
        setPropertyValue(item, 'Availability', String(cartLineItemAvailabilityMessage).replace(/<[^>]*>/g, ''));
      }
    } catch (error) {
      console.error('frequently-bought-together: availability enhancement failed', error);
    }
  }

  function getVariantFromItem(item) {
    return {
      sku: getPropertyValueFromItem(item, '_product_sku'),
      inventoryQuantity: Number(getPropertyValueFromItem(item, '_variant_available_quantity')) || 0,
      leadTime: getPropertyValueFromItem(item, '_lead_time'),
      isCustom: getPropertyValueFromItem(item, '_is_custom_order') === 'true',
    };
  }

  function getPropertyValueFromItem(item, propertyName) {
    const input = item.querySelector(`[data-fbt-property="${propertyName}"]`);
    return input ? input.value : '';
  }

  function renderPrice(item) {
    const priceEl = item.querySelector('[data-fbt-price]');
    if (!priceEl) return;

    const price = Number(item.dataset.priceCents) || 0;
    const compareAt = Number(item.dataset.compareAtPriceCents) || 0;

    if (compareAt > price) {
      priceEl.innerHTML = `
        <span class="frequently-bought-together__sale-price">${formatMoney(price)}</span>
        <span class="frequently-bought-together__compare-price">Was ${formatMoney(compareAt)}</span>
      `;
      return;
    }

    priceEl.innerHTML = `<span>${formatMoney(price)}</span>`;
  }

  function getCheckedItems(root) {
    return Array.from(root.querySelectorAll('[data-fbt-item]')).filter((item) => {
      const checkbox = item.querySelector('[data-fbt-checkbox]');
      return checkbox && checkbox.checked;
    });
  }

  function updateSubmitLabel(root, checkedCount) {
    const submit = root.querySelector('[data-fbt-submit]');
    const label = root.querySelector('[data-fbt-submit-label]');
    if (!submit || !label) return;

    submit.disabled = checkedCount === 0;

    if (checkedCount === 0) {
      label.textContent = 'Add to cart';
    } else if (checkedCount === 1) {
      label.textContent = 'Add to cart';
    } else if (checkedCount === 2) {
      label.textContent = 'Add both to cart';
    } else {
      label.textContent = 'Add all to cart';
    }
  }

  function updateSubtotal(root) {
    const checkedItems = getCheckedItems(root);
    const total = checkedItems.reduce((sum, item) => {
      const price = Number(item.dataset.priceCents) || 0;
      const quantity = Number(item.dataset.quantity) || 1;
      return sum + price * quantity;
    }, 0);

    const totalEl = root.querySelector('[data-fbt-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);

    updateSubmitLabel(root, checkedItems.length);
  }

  function setExpanded(root, expanded, persist) {
    const toggle = root.querySelector('[data-fbt-toggle]');
    const content = root.querySelector('[data-fbt-content]');
    if (!toggle || !content) return;

    content.hidden = false;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      expanded ? 'Close Frequently Purchased Together' : 'Open Frequently Purchased Together'
    );
    toggle.classList.toggle('expanded', expanded);
    content.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    content.style.overflow = 'hidden';

    if (expanded) {
      content.classList.add('expanded');
      content.style.height = `${content.scrollHeight}px`;
      window.setTimeout(() => finishExpandedContent(content), ACCORDION_TRANSITION_MS);
    } else {
      content.style.height = `${content.scrollHeight}px`;
      content.offsetHeight;
      content.classList.remove('expanded');
      content.style.height = '0';
    }

    if (persist) setCookie(COOKIE_NAME, expanded ? 'false' : 'true');
  }

  function applyCookieState(root) {
    const preference = getCookie(COOKIE_NAME);
    setExpanded(root, preference !== 'true', false);
  }

  async function syncItemFromVariant(item, variant, productPayload, root) {
    if (!item || !variant || !productPayload) return;

    const quantity = getLineQty(productPayload, variant);

    item.dataset.variantId = String(variant.id);
    item.dataset.priceCents = String(variant.price || 0);
    item.dataset.compareAtPriceCents = String(variant.compareAtPrice || 0);
    item.dataset.quantity = String(quantity);
    item.dataset.available = variant.available === false ? 'false' : 'true';

    syncItemImage(item, productPayload, variant);
    renderPrice(item);
    setPropertyValue(item, 'Availability', buildAvailabilityMessage(variant));
    setPropertyValue(item, '_product_sku', variant.sku || '');
    setPropertyValue(item, '_lead_time', variant.leadTime != null ? String(variant.leadTime) : '');
    setPropertyValue(item, '_is_custom_order', variant.isCustom === true ? 'true' : 'false');
    setPropertyValue(item, '_variant_available_quantity', Number(variant.inventoryQuantity) || 0);

    await refreshAvailabilityMessage(item, variant, quantity);
    if (root) updateSubtotal(root);
  }

  async function syncCurrentProduct(root, payload, variantId) {
    const currentItem = root.querySelector('[data-fbt-current-item="true"]');
    const productPayload = payload?.currentProduct;
    const variant = findCurrentProductVariant(payload, variantId);
    if (!currentItem || !productPayload || !variant) return;

    await syncItemFromVariant(currentItem, variant, productPayload, root);
  }

  function findFirstSellableVariant(productPayload) {
    const variants = productPayload?.variants || [];
    return variants.find((variant) => isVariantSellable(variant)) || variants[0] || null;
  }

  function findAutoMatchedVariant(mainVariant, mainOptions, compProduct, rules) {
    if (!mainVariant || !compProduct) return null;

    for (const rule of rules || []) {
      const sourceIndex = getOptionIndex(mainOptions, rule.sourceOptions);
      if (sourceIndex === -1) continue;

      const sourceValue = normalizeOptionName(getVariantOptionValue(mainVariant, sourceIndex));
      if (!sourceValue) continue;

      for (const targetName of rule.targetOptions || []) {
        const targetIndex = getOptionIndex(compProduct.options, [targetName]);
        if (targetIndex === -1) continue;

        const sellableMatch = findBestSellableMatchForTargetOption(
          sourceValue,
          compProduct.variants,
          targetIndex
        );
        if (sellableMatch) return sellableMatch;
      }
    }

    return null;
  }

  function resolveComplementaryVariant(item, compProduct, mainVariant, mainOptions, rules, userSnapshot) {
    const matched = findAutoMatchedVariant(mainVariant, mainOptions, compProduct, rules);
    if (matched) {
      return { variant: matched, reason: 'match' };
    }

    if (userSnapshot?.userSelected && userSnapshot.variantId) {
      const retained = findVariantInProductPayload(compProduct, userSnapshot.variantId);
      if (retained) {
        return { variant: retained, reason: 'retain-user' };
      }
    }

    const fallback = findFirstSellableVariant(compProduct);
    return { variant: fallback, reason: 'first-available' };
  }

  function snapshotComplementaryUserSelection(item) {
    return {
      userSelected: item.dataset.fbtUserSelected === 'true',
      variantId: item.dataset.variantId || item.querySelector('[data-fbt-variant-select]')?.value || null,
    };
  }

  async function syncComplementaryItem(root, payload, item, variant, options = {}) {
    const { updateSelect = false } = options;
    const productId = item.dataset.productId;
    const compProduct = findComplementaryProductPayload(payload, productId);
    if (!compProduct || !variant) return;

    if (updateSelect) {
      const select = item.querySelector('[data-fbt-variant-select]');
      if (select) {
        const option = Array.from(select.options).find((opt) => String(opt.value) === String(variant.id));
        if (option && !option.disabled) {
          item.dataset.fbtSyncing = 'true';
          select.value = String(variant.id);
          delete item.dataset.fbtSyncing;
        }
      }
    }

    await syncItemFromVariant(item, variant, compProduct, root);
  }

  async function autoMatchComplementaryItems(root, payload, mainVariant, userSnapshotsByProductId) {
    if (!mainVariant) return;

    const mainOptions = payload?.currentProduct?.options || [];
    const items = root.querySelectorAll('[data-fbt-complementary-item="true"]');

    for (const item of items) {
      const productId = item.dataset.productId;
      const compProduct = findComplementaryProductPayload(payload, productId);
      if (!compProduct) continue;

      const userSnapshot =
        userSnapshotsByProductId?.[productId] || snapshotComplementaryUserSelection(item);

      const resolved = resolveComplementaryVariant(
        item,
        compProduct,
        mainVariant,
        mainOptions,
        COMPLEMENTARY_OPTION_MATCH_RULES,
        userSnapshot
      );

      if (!resolved.variant) continue;

      if (resolved.reason === 'match' || resolved.reason === 'first-available') {
        delete item.dataset.fbtUserSelected;
        await syncComplementaryItem(root, payload, item, resolved.variant, { updateSelect: true });
      } else {
        item.dataset.fbtUserSelected = 'true';
        await syncComplementaryItem(root, payload, item, resolved.variant, { updateSelect: false });
      }
    }
  }

  function buildCartItem(item) {
    const properties = {};

    item.querySelectorAll('input[name^="properties["]').forEach((input) => {
      const propertyName = getPropertyName(input);
      if (!propertyName) return;
      properties[propertyName] = input.value || '';
    });

    return {
      id: Number(item.dataset.variantId),
      quantity: Number(item.dataset.quantity) || 1,
      properties,
    };
  }

  function setError(root, message) {
    const error = root.querySelector('[data-fbt-error]');
    if (!error) return;

    error.hidden = !message;
    error.textContent = message || '';
  }

  async function addCheckedItemsToCart(root) {
    const submit = root.querySelector('[data-fbt-submit]');
    const checkedItems = getCheckedItems(root);
    if (!submit || checkedItems.length === 0) return;

    const isBundle = checkedItems.length >= 2;
    const items = checkedItems
      .map((item) => {
        const cartItem = buildCartItem(item);
        if (isBundle && item.dataset.fbtComplementaryItem === 'true' && cartItem.id) {
          cartItem.properties = cartItem.properties || {};
          cartItem.properties._frequently_bought_together_bundle = 'true';
        }
        return cartItem;
      })
      .filter((item) => item.id);
    if (items.length === 0) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = {
      items,
      sections_url: window.location.pathname,
    };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    setError(root, '');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const parsed = await response.json();

      if (!response.ok || parsed.status) {
        throw new Error(parsed.description || parsed.message || 'Unable to add selected items to cart.');
      }

      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }

      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'frequently-bought-together', cartData: parsed });
      }
      cart.renderContents(parsed);
    } catch (error) {
      console.error('frequently-bought-together: add to cart failed', error);
      setError(root, error.message || 'Unable to add selected items to cart.');
    } finally {
      submit.removeAttribute('aria-busy');
      updateSubtotal(root);
    }
  }

  async function handleMainVariantChange(root, payload, event) {
    const variantId = getCurrentVariantId(event);
    if (!variantId) return;

    const mainVariant = findCurrentProductVariant(payload, variantId);
    if (!mainVariant) return;

    const snapshots = {};
    root.querySelectorAll('[data-fbt-complementary-item="true"]').forEach((item) => {
      snapshots[item.dataset.productId] = snapshotComplementaryUserSelection(item);
    });

    await syncCurrentProduct(root, payload, variantId);
    await autoMatchComplementaryItems(root, payload, mainVariant, snapshots);
  }

  function initRoot(root) {
    if (root.dataset.fbtInitialized === 'true') return;
    root.dataset.fbtInitialized = 'true';

    const payload = parsePayload(root);
    const content = root.querySelector('[data-fbt-content]');
    if (content) {
      content.addEventListener('transitionend', handleContentTransitionEnd);
    }

    applyCookieState(root);
    updateSubtotal(root);

    root.querySelectorAll('[data-fbt-checkbox]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => updateSubtotal(root));
    });

    root.querySelectorAll('[data-fbt-variant-select]').forEach((select) => {
      select.addEventListener('change', async (event) => {
        const item = event.currentTarget.closest('[data-fbt-complementary-item="true"]');
        if (!item || item.dataset.fbtSyncing === 'true') return;

        const compProduct = findComplementaryProductPayload(payload, item.dataset.productId);
        const variant = findVariantInProductPayload(compProduct, event.currentTarget.value);
        if (!variant) return;

        item.dataset.fbtUserSelected = 'true';
        await syncComplementaryItem(root, payload, item, variant, { updateSelect: false });
      });
    });

    const toggle = root.querySelector('[data-fbt-toggle]');
    if (toggle) {
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        setExpanded(root, expanded, true);
      });
    }

    const submit = root.querySelector('[data-fbt-submit]');
    if (submit) {
      submit.addEventListener('click', () => addCheckedItemsToCart(root));
    }

    const bootstrap = async () => {
      const currentVariantId = getCurrentVariantId();
      const mainVariant = currentVariantId
        ? findCurrentProductVariant(payload, currentVariantId)
        : null;

      if (currentVariantId) {
        await syncCurrentProduct(root, payload, currentVariantId);
      }

      if (mainVariant) {
        await autoMatchComplementaryItems(root, payload, mainVariant);
      } else {
        root.querySelectorAll('[data-fbt-complementary-item="true"]').forEach((item) => {
          refreshAvailabilityMessage(item, getVariantFromItem(item), Number(item.dataset.quantity) || 1);
        });
      }
    };

    bootstrap();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        handleMainVariantChange(root, payload, event);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-fbt-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
